/* eslint-disable no-console */
import {Flex, Table} from 'antd';
import React from 'react';

import BoxContent from '../../../../components/BoxContent/BoxContent';
import MainTableTitle from '../../../../components/Table/Utils/MainTableTitle';
import sumTableValue from '../../../../components/Table/Utils/sumTableValue';
import {sortBy} from '../../../../shared/commonFunctions';
import {convertPxToRem} from '../../../../utils/commonFunctions';
import {formatIndian} from '../../../../utils/formatIndian';
import {filterMainAndSubData} from '../../constants/commonFunctions';
import {allSupplierType} from '../../generalized/interface/IGeneralized';
import FacilityCount from '../../generalized/utils/FacilityCount';
import {returnMaxAndMin} from '../../utils/commonFunctions';

const AllSuppliers: React.FC<{
  data: any[];
  keyArray: string[];
  tooltip?: string;
  type?: allSupplierType;
  customTitle?: string;
  sumOfKey?: string;
}> = ({
  data,
  keyArray,
  tooltip = '',
  type = 'supplyChain',
  customTitle = '',
  sumOfKey = ''
}) => {
  const {mainData, subData} = filterMainAndSubData(data, 'dataId', keyArray);
  const {max} = returnMaxAndMin(subData, 'emissions');

  const columnsSwitch = (type: allSupplierType) => {
    switch (type) {
      case 'supplyChain':
        return {
          scroll: 440,
          columns: [
            {
              title: 'Supplier Name',
              dataIndex: 'supplierName'
            },
            {
              title: 'Type of Supplier',
              dataIndex: 'typeOfSupplier'
            },
            {
              title: 'Source',
              dataIndex: 'source'
            },
            {
              title: 'Distance from Facility (Km)',
              dataIndex: 'kmFromFacility'
            },
            {
              title: 'Emission (tCO2e)',
              dataIndex: 'emissions',
              render: (value: any) => {
                const maxAdded = max.emissions * 1.5;
                const mainValue = `${(value / maxAdded) * 100}%`;

                return (
                  <div style={{width: '100%', height: '100%'}}>
                    <div
                      style={{
                        backgroundColor: value === 0 ? '#fff' : '#5697F8',
                        width: `${mainValue}`,
                        minWidth: '20%'
                      }}
                    >
                      <div
                        style={{
                          font: 'normal normal 500 14px/22px Montserrat',
                          color: value === 0 ? '' : '#fff',
                          marginLeft: '0.3rem',
                          marginRight: '0.3rem',
                          width: '100%'
                        }}
                      >
                        {formatIndian(value)}
                      </div>
                    </div>
                  </div>
                );
              }
            }
          ]
        };
      case 'asset':
        return {
          scroll: 680,
          columns: [
            {title: 'Franchises', dataIndex: 'franchise'},
            {
              title: 'Waste Generated (Ton)',
              dataIndex: 'wasteGenerated',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Emissions (tCO2e)',
              dataIndex: 'emissions',
              render: (value: number) => {
                return <div>{`${formatIndian(value)}`}</div>;
              }
            }
          ]
        };
      case 'productMain':
        return {
          scroll: 380,
          columns: [
            {
              title: 'Product Name',
              dataIndex: 'productName'
            },
            {
              title: 'Unit (No.)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Raw Material Emissions (tCO2e)',
              dataIndex: 'rawMaterial',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Processing Emissions (tCO2e)',
              dataIndex: 'processing',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Product Use Emissions (tCO2e)',
              dataIndex: 'productUse',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Product End of Life Emissions (tCO2e)',
              dataIndex: 'endOfLifeTreatment',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Per Unit Emissions (tCO2e)',
              dataIndex: 'perUnitEmission',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'productSub':
        return {
          scroll: 410,
          columns: [
            {
              title: 'Raw Material Name',
              dataIndex: 'rawMaterialName'
            },
            {
              title: 'Quantity (Ton)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'fuelUtility':
        return {
          scroll: 540,
          columns: [
            {
              title: 'Vehicle Type',
              dataIndex: 'vehicleName',
              width: 200
            },
            {
              title: 'No.',
              dataIndex: 'numberOfUnit',
              render: (value: number) => {
                return formatIndian(value);
              }
            },
            {
              title: 'Fuel',
              dataIndex: 'fuelName'
            },
            {
              title: 'Unit (Ton)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'fuelUtilityScope1':
        return {
          scroll: 590,
          columns: [
            {title: 'Facility Name', dataIndex: 'facility'},
            {
              title: 'Unit (tCO2e)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'electricFacility':
        return {
          scroll: 400,
          columns: [
            {
              title: 'Facility Name',
              dataIndex: 'facility'
            },
            {
              title: 'Unit (tCO2e)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'waterCost':
        return {
          scroll: 400,
          columns: [
            {
              title: 'Facility Name',
              dataIndex: 'facility'
            },
            {
              title: 'Cost (₹)',
              dataIndex: 'value',
              render: (value: any) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'waterFacility':
        return {
          scroll: 400,
          columns: [
            {
              title: 'Facility Name',
              dataIndex: 'facility'
            },
            {
              title: 'Unit (KL)',
              dataIndex: 'value',
              render: (value: any) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'waterOps':
        return {
          scroll: 400,
          columns: [
            {
              title: 'Facility Name',
              dataIndex: 'facility'
            },
            {
              title: 'KL/ Sq ft',
              dataIndex: 'value',
              render: (value: any) => {
                return formatIndian(value);
              }
            }
          ]
        };
    }
  };

  const getLeasedData = (upstream: boolean) => {
    return (
      subData
        ?.map((item, index) => {
          return {...item, key: index};
        })
        ?.filter((item) => {
          const isUpstream = upstream ? 'Upstream' : 'Downstream';

          return item?.typeOfSupplier === isUpstream;
        }) ?? []
    );
  };

  const upstream = getLeasedData(true);
  const downstream = getLeasedData(false);
  const {columns, scroll} = columnsSwitch(type);

  return (
    <BoxContent>
      <Flex vertical={true} gap="1rem">
        <MainTableTitle
          title={mainData?.label ?? 'All Facility'}
          showDivider={false}
          tooltip={tooltip}
        />
        {type === 'supplyChain' ? (
          <FacilityCount
            subData={subData}
            leased={upstream}
            nonLeased={downstream}
            isSingle={true}
            customTitle={customTitle}
            isStream={true}
            mainData={mainData}
          />
        ) : (
          <Flex
            gap="1rem"
            style={{
              padding: '0.5rem',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'flex-start'
            }}
          >
            <img
              src={mainData?.image ?? ''}
              style={{width: convertPxToRem(63), height: convertPxToRem(63)}}
            />
            <Flex vertical={true}>
              <div
                style={{
                  font: 'normal normal 600 12px/15px Montserrat',
                  color: '#99B1C7'
                }}
              >
                {customTitle !== '' ? customTitle : 'TOTAL NO. OF FRANCHISEES'}
              </div>
              <div
                style={{
                  font: 'normal normal 600 24px/29px Montserrat',
                  color: '#677489'
                }}
              >
                {sumOfKey === ''
                  ? subData?.length ?? 0
                  : sumTableValue(subData, sumOfKey)}
                <span>
                  {mainData?.unit !== 'NA'
                    ? ` ${mainData?.unit ?? ''}`
                    : ' No.'}
                </span>
              </div>
            </Flex>
          </Flex>
        )}
        <div style={{height: '100%'}}>
          <Table
            dataSource={sortBy(subData, 'emissions')?.map((item, index) => {
              return {...item, key: index};
            })}
            columns={columns}
            scroll={{y: scroll}}
            rowKey="key"
            pagination={false}
            style={{
              height: '100%'
            }}
          />
        </div>
      </Flex>
    </BoxContent>
  );
};

export default AllSuppliers;
